import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { authService } from '../services/authService';
import { studentAPI, userAPI } from '../services/api';
import { clearTokenCache } from '../services/tokenManager';
import { deactivateCurrentDevice } from '../services/fcmService';
import { User, Student } from '../types';
import { Logger } from '../utils/logger';
import { AuthError, childrenRefusal, meRefusal } from '../utils/portalAccess';

export type { AuthError } from '../utils/portalAccess';

// Backend returns a StudentFullSnapshotDto per child (Profile + Enrollments[] + Relatives[]).
// The app works with a flattened Student built from the profile + the active enrollment.
function mapSnapshotToStudent(snapshot: any): Student {
  const enrollments = snapshot.Enrollments ?? snapshot.enrollments ?? [];

  // A student can carry more than one IsActive enrollment at once - e.g. next
  // year's class is pre-enrolled (and marked active) before the current one
  // ends. Picking "the first active one" then depends on array order, and can
  // resolve to a class the child isn't in yet - which silently points every
  // class-scoped fetch (homework, timetable, ...) at the wrong class. Prefer
  // whichever active enrollment matches THIS academic year.
  const currentYear = new Date().getFullYear();
  const activeEnrollments = enrollments.filter((e: any) => e.IsActive ?? e.isActive);
  const enrollment =
    activeEnrollments.find((e: any) => (e.AcademicYear ?? e.academicYear) === currentYear) ??
    activeEnrollments[0] ??
    enrollments[0] ??
    {};
  const profile = snapshot.Profile ?? snapshot.profile ?? {};

  const firstName = profile.FirstName ?? profile.firstName;
  const middleName = profile.MiddleName ?? profile.middleName;
  const lastName = profile.LastName ?? profile.lastName;
  const fullName = [firstName, middleName, lastName]
    .filter(Boolean)
    .join(' ');

  return {
    id: enrollment.StudentId ?? enrollment.studentId ?? profile.Id ?? profile.id ?? '',
    name: fullName || 'Student',
    rollNumber: enrollment.RollNumber ?? enrollment.rollNumber ?? '',
    class: enrollment.ClassDisplayName ?? enrollment.classDisplayName ?? '',
    classId: enrollment.ClassId ?? enrollment.classId ?? '',
    enrollmentId: enrollment.Id ?? enrollment.id ?? '',
    academicYear: enrollment.AcademicYear ?? enrollment.academicYear ?? new Date().getFullYear(),
    section: '',
    schoolName: '',
    parentId: '',
    studentIdentificationNumber: profile.StudentIdentificationNumber ?? profile.studentIdentificationNumber ?? '',
    address1: profile.Address1 ?? profile.address1,
    address2: profile.Address2 ?? profile.address2,
    district: profile.District ?? profile.district,
    state: profile.State ?? profile.state,
    country: profile.Country ?? profile.country,
    pincode: profile.Pincode ?? profile.pincode,
    // TODO(TESTING ONLY): hardcoded to false to force the onboarding wizard to trigger
    // regardless of the real backend value. Revert to the commented line before shipping.
    isFeeStructureSelected: enrollment.IsFeeStructureSelected ?? enrollment.isFeeStructureSelected ?? false,
    // isFeeStructureSelected: false,
    academicFeeStructureId: enrollment.AcademicFeeStructureId ?? enrollment.academicFeeStructureId ?? null,
    rawSnapshot: snapshot,
  };
}

// The profile-completion popup gates purely on whether any relative is flagged as the
// emergency contact (Relatives[].IsEmergencyContact) — no phone-number check.
export function hasEmergencyContact(student: Student | null | undefined): boolean {
  if (!student) return false;
  const snapshot = student.rawSnapshot ?? {};
  const relatives = snapshot.Relatives ?? snapshot.relatives ?? [];
  return (relatives as any[]).some((r: any) => !!(r.IsEmergencyContact ?? r.isEmergencyContact));
}

interface AuthContextType {
  user: User | null;
  students: Student[];
  selectedStudent: Student | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  authError: AuthError;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  selectStudent: (student: Student) => void;
  refreshSelectedStudent: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<AuthError>(null);

  useEffect(() => {
    restoreSession();
  }, []);

  const loadChildren = async (parentUser: User) => {
    try {
      Logger.debug('Auth', `Loading children for user: ${parentUser.email}`);

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Children loading timeout')), 10000)
      );

      const childrenPromise = studentAPI.getMyChildren();
      const { data } = await Promise.race([childrenPromise, timeoutPromise]) as any;
      const mapped: Student[] = (data ?? []).map(mapSnapshotToStudent);
      setStudents(mapped);
      Logger.info('Auth', `✓ Loaded ${mapped.length} child(ren) for ${parentUser.email}`);

      // A cached selection is only valid if it's actually one of THIS user's freshly
      // fetched children — otherwise a stale/empty-id entry from an earlier session
      // (different account, older app version, etc.) gets reused forever, since it's
      // never re-derived from the real API response once cached.
      const storedStudent = await AsyncStorage.getItem('selectedStudent');
      const parsedStored: Student | null = storedStudent ? JSON.parse(storedStudent) : null;
      const freshStudent =
        parsedStored?.id ? mapped.find(s => s.id === parsedStored.id) : null;

      if (freshStudent) {
        setSelectedStudent(freshStudent);
        await AsyncStorage.setItem('selectedStudent', JSON.stringify(freshStudent));
        Logger.debug('Auth', `Selected stored student: ${freshStudent.name}`);
      } else if (mapped.length > 0) {
        setSelectedStudent(mapped[0]);
        await AsyncStorage.setItem('selectedStudent', JSON.stringify(mapped[0]));
        Logger.debug('Auth', `Selected first student: ${mapped[0].name}`);
      } else {
        setSelectedStudent(null);
        await AsyncStorage.removeItem('selectedStudent');
        Logger.warn('Auth', 'No children found for user');
      }
    } catch (err) {
      Logger.error('Auth', 'Failed to load children', err);
      setStudents([]);
      setSelectedStudent(null);
      // The children list is the whole app — proceeding without it just moves
      // the failure onto every screen. Gate at the door instead, saying why
      // (not invited, switched off, nothing linked) when the API says so.
      setAuthError(childrenRefusal(err));
    }
  };

  const restoreSession = async () => {
    Logger.info('Auth', '⏳ Restoring session...');
    try {
      // Back from Microsoft's full-page sign-in (web, when the popup was blocked): finish
      // signing in exactly as after the popup. The spinner stays up until that is done.
      const returned = await authService.takeRedirectSignIn();
      if (returned) {
        await finishSignIn(returned.idToken);
        return;
      }

      try {
        Logger.debug('Auth', 'Checking for access token');
        const accessToken = await authService.getAccessToken();

        if (!accessToken) {
          Logger.debug('Auth', 'No active session — showing login');
          setIsLoading(false);
          return;
        }

        Logger.debug('Auth', '✓ Access token found, retrieving user data');
        const storedUser = await AsyncStorage.getItem('user');

        if (!storedUser) {
          Logger.warn('Auth', 'Access token exists but no stored user');
          setIsLoading(false);
          return;
        }

        try {
          const parsedUser: User = JSON.parse(storedUser);
          let refused = false;

          // --- Mirror the frontend _app.tsx pattern ---
          // Re-call /User/me on every app load so hasWebAccess is always
          // the live server value, not a stale cached value.
          try {
            Logger.info('Auth', '⏳ Re-fetching /User/me to refresh access flags...');
            const apiAccessToken = await authService.getApiAccessToken();
            if (apiAccessToken) {
              const { data: userDetails } = await userAPI.getMeWithToken(apiAccessToken);
              // API may return either camelCase or PascalCase — handle both
              const hasWebAccess = userDetails.hasWebAccess ?? userDetails.HasWebAccess;
              const hasMobileAccess = userDetails.hasMobileAccess ?? userDetails.HasMobileAccess;
              Logger.debug('Auth', '✓ /User/me refreshed', { hasWebAccess, hasMobileAccess });
              // Merge the fresh server values into the cached user
              Object.assign(parsedUser, {
                userId: userDetails.userId ?? userDetails.UserId,
                externalId: userDetails.externalId ?? userDetails.ExternalId,
                username: userDetails.username ?? userDetails.Username,
                isActive: userDetails.isActive ?? userDetails.IsActive,
                isEmployee: userDetails.isEmployee ?? userDetails.IsEmployee,
                permissions: userDetails.permissions ?? userDetails.Permissions,
                globalRole: userDetails.globalRole ?? userDetails.GlobalRole,
                moduleRole: userDetails.moduleRole ?? userDetails.ModuleRole,
                hasWebAccess,
                hasMobileAccess,
              });
              // Persist refreshed flags so the next session restore also has them
              await AsyncStorage.setItem('user', JSON.stringify(parsedUser));
            }
          } catch (meErr: any) {
            if (meErr?.response) {
              // The server answered and refused (401/403/5xx): access flags are
              // unknowable, so the app must not proceed on stale cached ones.
              Logger.error('Auth', '/User/me failed on restore — blocking access', meErr);
              setAuthError(meRefusal(meErr));
              refused = true;
            } else {
              // No response = offline/unreachable. Cached flags are the best
              // available answer; locking a parent out for a network blip is worse.
              Logger.warn('Auth', 'Failed to refresh /User/me on restore — using cached flags', meErr);
            }
          }
          setUser(parsedUser);
          Logger.info('Auth', `✓ Session restored for ${parsedUser.email}`);
          // Refused already: the first reason is the one shown.
          if (!refused) await loadChildren(parsedUser);
        } catch (parseErr) {
          Logger.error('Auth', 'Failed to parse stored user', parseErr);
          await AsyncStorage.removeItem('user');
        }
      } catch (tokenErr) {
        Logger.error('Auth', 'Token check failed (likely first launch)', tokenErr);
        // First launch or no saved token - show login
        setIsLoading(false);
        return;
      }
    } catch (err) {
      Logger.error('Auth', 'Session restore failed', err);
      setIsLoading(false);
    } finally {
      // Always ensure loading is false
      setIsLoading(false);
      Logger.debug('Auth', 'Session restore complete');
    }
  };

  /**
   * Everything after Microsoft has said who this is: /User/me for the access flags, the saved
   * user and the children. The same after the popup and after a full-page sign-in.
   */
  const finishSignIn = async (idToken: string) => {
    const claims = decodeIdTokenClaims(idToken);
    Logger.debug('Auth', 'Claims decoded', { email: claims.preferred_username, name: claims.name });

    const loggedInUser: User = {
      id: claims.oid ?? claims.sub ?? '',
      name: claims.name ?? claims.email ?? 'Parent',
      email: claims.preferred_username ?? claims.email ?? '',
      role: 'parent',
    };

    // Fetch user details from /api/User/me to get role and permissions
    let refused = false;
    try {
      Logger.info('Auth', '⏳ Fetching user details from /api/User/me...');

      // Get API access token with proper API scope (not OIDC token)
      const apiAccessToken = await authService.getApiAccessToken();
      if (!apiAccessToken) {
        throw new Error('Failed to obtain API access token');
      }

      Logger.debug('Auth', '📋 API Bearer token:', {
        hasToken: !!apiAccessToken,
        tokenLength: apiAccessToken?.length ?? 0,
        tokenPrefix: apiAccessToken?.substring(0, 20) ?? 'none',
        isJWT: apiAccessToken?.startsWith('eyJ') ? 'YES (valid JWT)' : 'NO (invalid format)'
      });

      const { data: userDetails } = await userAPI.getMeWithToken(apiAccessToken);

      // API may return either camelCase or PascalCase — handle both
      const hasWebAccess = userDetails.hasWebAccess ?? userDetails.HasWebAccess;
      const hasMobileAccess = userDetails.hasMobileAccess ?? userDetails.HasMobileAccess;
      Logger.debug('Auth', '✓ User details received', {
        userId: userDetails.userId ?? userDetails.UserId,
        email: userDetails.email ?? userDetails.Email,
        isEmployee: userDetails.isEmployee ?? userDetails.IsEmployee,
        permissions: (userDetails.permissions ?? userDetails.Permissions)?.length ?? 0,
        globalRole: userDetails.globalRole ?? userDetails.GlobalRole,
        moduleRole: userDetails.moduleRole ?? userDetails.ModuleRole,
        hasWebAccess,
        hasMobileAccess,
      });

      // Merge user details into loggedInUser
      Object.assign(loggedInUser, {
        userId: userDetails.userId ?? userDetails.UserId,
        externalId: userDetails.externalId ?? userDetails.ExternalId,
        username: userDetails.username ?? userDetails.Username,
        isActive: userDetails.isActive ?? userDetails.IsActive,
        isEmployee: userDetails.isEmployee ?? userDetails.IsEmployee,
        permissions: userDetails.permissions ?? userDetails.Permissions,
        globalRole: userDetails.globalRole ?? userDetails.GlobalRole,
        moduleRole: userDetails.moduleRole ?? userDetails.ModuleRole,
        hasWebAccess,
        hasMobileAccess,
        reporteeCount: userDetails.ReporteeCount,
        employeeId: userDetails.EmployeeId,
        employeeCode: userDetails.EmployeeCode,
        firstName: userDetails.FirstName,
        lastName: userDetails.LastName,
        dateOfBirth: userDetails.DateOfBirth,
        dateOfJoining: userDetails.DateOfJoining,
        employmentType: userDetails.EmploymentType,
        gender: userDetails.Gender,
        phoneNumber: userDetails.PhoneNumber,
        address: userDetails.Address,
        departmentId: userDetails.DepartmentId,
        departmentName: userDetails.DepartmentName,
        reportingManagerId: userDetails.ReportingManagerId,
        reportingManagerName: userDetails.ReportingManagerName,
      });

      Logger.info('Auth', `✓ User authenticated: ${loggedInUser.email} (Role: ${userDetails.GlobalRole || 'none'})`);
    } catch (userDetailErr) {
      // Without /User/me the access flags are unknown — the navigator shows
      // Access Denied rather than letting an unverified session in. No account
      // at all (401/404) means nobody invited this email.
      Logger.error('Auth', 'Failed to fetch user details from /api/User/me — blocking access', userDetailErr);
      setAuthError(meRefusal(userDetailErr));
      refused = true;
    }

    await AsyncStorage.setItem('user', JSON.stringify(loggedInUser));
    setUser(loggedInUser);

    // Refused already: the first reason is the one shown.
    if (!refused) await loadChildren(loggedInUser);
  };

  const login = async () => {
    try {
      // Loading stays true through sign-in → /User/me → /Parent, so the app
      // shows one spinner until it actually has everything it needs to render.
      setIsLoading(true);
      setAuthError(null);
      Logger.info('Auth', '⏳ Starting Azure AD sign-in...');
      const { idToken } = await authService.signIn();
      Logger.debug('Auth', '✓ ID token received');
      Logger.debug('Auth', '✓ Access token received');
      await finishSignIn(idToken);
      setIsLoading(false);
    } catch (err) {
      Logger.error('Auth', 'Sign-in failed', err);
      setIsLoading(false);
      throw err;
    }
  };

  const logout = async () => {
    // First, while the access token is still usable: tell the server this
    // handset is signing out. Afterwards the call cannot be authorised, and the
    // device would keep receiving push notifications about the child.
    await deactivateCurrentDevice();

    try {
      Logger.info('Auth', '⏳ Logging out...');
      await authService.signOut();
      Logger.debug('Auth', '✓ Remote sign-out succeeded');
    } catch (err) {
      Logger.warn('Auth', 'Remote sign-out failed (clearing local session)', err);
    }

    try {
      await clearTokenCache();
      await AsyncStorage.multiRemove(['user', 'selectedStudent']);
      Logger.debug('Auth', '✓ Local session and token cache cleared');
    } catch (err) {
      Logger.error('Auth', 'Failed to clear local session', err);
    }

    setUser(null);
    setStudents([]);
    setSelectedStudent(null);
    setAuthError(null);
    Logger.info('Auth', '✓ Logout complete');
  };

  const selectStudent = async (student: Student) => {
    try {
      Logger.debug('Auth', `Selecting student: ${student.name}`);
      setSelectedStudent(student);
      await AsyncStorage.setItem('selectedStudent', JSON.stringify(student));
      Logger.debug('Auth', `✓ Student selected: ${student.name} (${student.class})`);
    } catch (err) {
      Logger.error('Auth', 'Failed to select student', err);
    }
  };

  const refreshSelectedStudent = async () => {
    if (!selectedStudent?.id) return;
    try {
      Logger.debug('Auth', `Refreshing student: ${selectedStudent.id}`);
      // Reuse the same endpoint/shape as initial load (getMyChildren returns the
      // full snapshot this screen's rawSnapshot relies on) rather than /student/{id},
      // whose response shape isn't guaranteed to match mapSnapshotToStudent's expectations.
      const { data } = await studentAPI.getMyChildren();
      const mapped: Student[] = (data ?? []).map(mapSnapshotToStudent);
      const fresh = mapped.find(s => s.id === selectedStudent.id);
      if (fresh) {
        setStudents(mapped);
        setSelectedStudent(fresh);
        await AsyncStorage.setItem('selectedStudent', JSON.stringify(fresh));
        Logger.debug('Auth', `✓ Student refreshed: ${fresh.name}`);
      }
    } catch (err) {
      Logger.error('Auth', 'Failed to refresh student', err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        students,
        selectedStudent,
        isLoading,
        isAuthenticated: !!user,
        authError,
        login,
        logout,
        selectStudent,
        refreshSelectedStudent,
      }}>
      {children}
    </AuthContext.Provider>
  );
}

const BASE64_CHARS =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

function base64Decode(input: string): string {
  const clean = input.replace(/[^A-Za-z0-9+/]/g, '');
  let output = '';
  for (let i = 0; i < clean.length; i += 4) {
    const enc1 = BASE64_CHARS.indexOf(clean[i]);
    const enc2 = BASE64_CHARS.indexOf(clean[i + 1]);
    const enc3 = BASE64_CHARS.indexOf(clean[i + 2]);
    const enc4 = BASE64_CHARS.indexOf(clean[i + 3]);
    const chr1 = (enc1 << 2) | (enc2 >> 4);
    const chr2 = ((enc2 & 15) << 4) | (enc3 >> 2);
    const chr3 = ((enc3 & 3) << 6) | enc4;
    output += String.fromCharCode(chr1);
    if (enc3 !== -1) output += String.fromCharCode(chr2);
    if (enc4 !== -1) output += String.fromCharCode(chr3);
  }
  return output;
}

function decodeIdTokenClaims(idToken: string): Record<string, string> {
  try {
    const payload = idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      base64Decode(payload)
        .split('')
        .map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
}

export const useAuth = () => useContext(AuthContext);
